import { ArrowPathIcon, ChevronRightIcon } from '@heroicons/react/24/outline'

import { Skeleton } from './Skeleton'

import type { ReactNode } from 'react'

type SettingsRowProps = {
    icon: ReactNode
    title: string
    description?: ReactNode
    status?: string
    statusTone?: 'neutral' | 'success'
    onAction?: () => void
    actionLabel?: string
    disabled?: boolean
    pending?: boolean
}

const rowClasses =
    'border-account-divider flex w-full items-center gap-3 border-b px-4 py-4 text-left last:border-b-0 sm:gap-4 sm:px-5'

export const SettingsRow = ({
    icon,
    title,
    description,
    status,
    statusTone = 'neutral',
    onAction,
    actionLabel,
    disabled,
    pending,
}: SettingsRowProps) => {
    let statusClass = 'text-account-secondary'

    if (statusTone === 'success') {
        statusClass = 'text-account-success'
    }

    const content = (
        <>
            <span className="text-account-secondary flex h-8 w-7 shrink-0 items-center justify-center">{icon}</span>
            <span className="min-w-0 flex-1">
                <span className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                    <span className="text-account-strong text-sm font-semibold">{title}</span>
                    {status && <span className={`text-xs sm:hidden ${statusClass}`}>{status}</span>}
                </span>
                {description && (
                    <span className="text-account-secondary mt-1 block break-words text-sm">{description}</span>
                )}
            </span>
            {status && <span className={`hidden shrink-0 text-xs sm:inline ${statusClass}`}>{status}</span>}
            {pending ? (
                <ArrowPathIcon className="text-account-secondary h-4 w-4 shrink-0 animate-spin" aria-hidden="true" />
            ) : onAction ? (
                <ChevronRightIcon className="text-account-faint h-4 w-4 shrink-0" aria-hidden="true" />
            ) : null}
        </>
    )

    if (onAction) {
        return (
            <button
                type="button"
                onClick={onAction}
                disabled={disabled}
                aria-label={actionLabel}
                aria-busy={pending}
                className={`${rowClasses} hover:bg-account-muted focus-visible:outline-account-accent transition focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 disabled:cursor-not-allowed disabled:opacity-60`}
            >
                {content}
            </button>
        )
    }

    return <div className={rowClasses}>{content}</div>
}

export const SettingsRowSkeleton = () => (
    <div className={rowClasses} aria-busy="true">
        <Skeleton className="h-7 w-7 shrink-0" />
        <div className="min-w-0 flex-1 space-y-2">
            <Skeleton className="h-4 w-32 max-w-full" />
            <Skeleton className="h-4 w-56 max-w-full" />
        </div>
    </div>
)
